/**
 * Chat Header Component
 *
 * Displays agent info (avatar, name, role), expand/collapse, a three-dots menu,
 * and a close button.
 *
 * Avatar resolution (backward compatible):
 * - If agentInfo.avatarUrl exists -> show the image (with icon fallback on error).
 * - Otherwise -> show an inline SVG icon based on avatarIndex (no Lucide dep).
 */

import { useState, useRef, useEffect } from 'react';
import type { CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { AgentInfo, Labels } from '../types/widget';

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className="lc-w-5 lc-h-5">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const ExpandIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className="lc-w-5 lc-h-5">
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
);

const CollapseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" className="lc-w-5 lc-h-5">
      <path d="M8 3v3a2 2 0 0 1-2 2H3" />
      <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
      <path d="M3 16h3a2 2 0 0 1 2 2v3" />
      <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
);

const DotsIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="lc-w-5 lc-h-5">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
);

const NewChatIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" className="lc-w-4 lc-h-4">
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5 8.5 8.5 0 0 1 12.5 3a8.38 8.38 0 0 1 8.5 8.5z" />
      <line x1="12.5" y1="8" x2="12.5" y2="14" />
      <line x1="9.5" y1="11" x2="15.5" y2="11" />
    </svg>
);

const ClearIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" className="lc-w-4 lc-h-4">
      <path d="M3 6h18" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);

/**
 * Inline avatar icon set (subset of the dashboard's preset icons).
 * Kept inline to avoid adding Lucide as a widget dependency.
 */
function AvatarIcon({ index }: { index: number }) {
  const common = {
    viewBox: '0 0 24 24',
    className: 'lc-avatar-svg',
  } as const;

  switch (index % 12) {
    case 1: // User
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
      );
    case 2: // Heart
      return (
          <svg {...common} fill="currentColor" stroke="none">
            <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7z" />
          </svg>
      );
    case 3: // Star
      return (
          <svg {...common} fill="currentColor" stroke="none">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
      );
    case 4: // Zap
      return (
          <svg {...common} fill="currentColor" stroke="none">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
      );
    case 5: // Shield
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
      );
    case 6: // Coffee
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 8h1a4 4 0 0 1 0 8h-1" />
            <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
            <line x1="6" y1="2" x2="6" y2="4" />
            <line x1="10" y1="2" x2="10" y2="4" />
            <line x1="14" y1="2" x2="14" y2="4" />
          </svg>
      );
    case 7: // Rocket
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
            <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
          </svg>
      );
    case 8: // Crown
      return (
          <svg {...common} fill="currentColor" stroke="none">
            <path d="M2 6l4 4 4-6 4 6 4-4 2 13H2z" />
          </svg>
      );
    case 9: // Sparkles
      return (
          <svg {...common} fill="currentColor" stroke="none">
            <path d="M12 3l-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
          </svg>
      );
    case 10: // Circle
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
          </svg>
      );
    case 11: // Target
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="5.5" />
            <circle cx="12" cy="12" r="2" />
          </svg>
      );
    case 0: // Bot (default)
    default:
      return (
          <svg {...common} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <circle cx="12" cy="5" r="2" />
            <path d="M12 7v4" />
            <line x1="8" y1="16" x2="8" y2="16" strokeWidth="3" />
            <line x1="16" y1="16" x2="16" y2="16" strokeWidth="3" />
          </svg>
      );
  }
}

interface ChatHeaderProps {
  agentInfo: AgentInfo | null;
  onClose: () => void;
  labels: Labels;
  primaryColor?: string;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onRequestNewChat?: () => void;
  onClearMessages?: () => void;
}

export function ChatHeader({
                             agentInfo,
                             onClose,
                             labels,
                             primaryColor = '#FF3E6C',
                             isExpanded = false,
                             onToggleExpand,
                             onRequestNewChat,
                             onClearMessages,
                           }: ChatHeaderProps) {
  const avatarIndex = agentInfo?.avatarIndex ?? 0;
  // Avatar fallback derives from the configured brand color (no hardcoded colors).
  const avatarColor = primaryColor;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  const menuWrapRef = useRef<HTMLDivElement | null>(null);

  const hasImage = Boolean(agentInfo?.avatarUrl) && !imgError;

  // Close the menu on any click/tap outside it. Uses composedPath so it works
  // correctly inside the widget's Shadow DOM.
  useEffect(() => {
    if (!isMenuOpen) return;

    const handlePointer = (event: Event) => {
      const path = event.composedPath();
      if (menuWrapRef.current && !path.includes(menuWrapRef.current)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('touchstart', handlePointer);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('touchstart', handlePointer);
    };
  }, [isMenuOpen]);

  const headerBtnClass =
      'lc-p-2 lc-rounded-lg lc-bg-transparent lc-border-none lc-text-white lc-cursor-pointer hover:lc-bg-white/20 lc-transition-colors';

  return (
      <div
          className="lc-flex lc-items-center lc-justify-between lc-px-4 lc-py-4 lc-border-b lc-border-white/10"
          style={{ backgroundColor: primaryColor }}
      >
        <div className="lc-flex lc-items-center lc-gap-1">
          <div className="lc-relative" ref={menuWrapRef}>
            <button
                type="button"
                onClick={() => setIsMenuOpen((p) => !p)}
                aria-label={labels.menu}
                aria-haspopup="menu"
                aria-expanded={isMenuOpen}
                className={headerBtnClass}
            >
              <DotsIcon />
            </button>

            <AnimatePresence>
              {isMenuOpen && (
                  <motion.div
                      role="menu"
                      className="lc-menu"
                      style={{ '--lc-primary': primaryColor } as CSSProperties}
                      initial={{ opacity: 0, y: -6, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -6, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                  >
                    <button
                        type="button"
                        role="menuitem"
                        className="lc-menu-item"
                        onClick={() => {
                          setIsMenuOpen(false);
                          onRequestNewChat?.();
                        }}
                    >
                      <NewChatIcon />
                      <span>{labels.menuNewChat}</span>
                    </button>
                    <button
                        type="button"
                        role="menuitem"
                        className="lc-menu-item"
                        onClick={() => {
                          setIsMenuOpen(false);
                          onClearMessages?.();
                        }}
                    >
                      <ClearIcon />
                      <span>{labels.menuClearChat}</span>
                    </button>
                  </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
              type="button"
              onClick={onToggleExpand}
              aria-label={isExpanded ? labels.collapse : labels.expand}
              className={headerBtnClass}
          >
            {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
          </button>

          <button
              type="button"
              onClick={onClose}
              aria-label="סגור צ'אט"
              className={headerBtnClass}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="lc-flex lc-items-center lc-gap-3">
          <div className="lc-flex lc-flex-col lc-items-end">
            <h2 className="lc-text-white lc-font-semibold lc-text-base lc-m-0">
              {agentInfo?.name || labels.chatHeader}
            </h2>
            {agentInfo?.role && (
                <span className="lc-text-white/80 lc-text-xs">{agentInfo.role}</span>
            )}
          </div>

          <div
              className="lc-avatar-ring"
              style={{ backgroundColor: hasImage ? '#ffffff' : avatarColor }}
          >
            {hasImage ? (
                <img
                    src={agentInfo!.avatarUrl}
                    alt=""
                    className="lc-avatar-img"
                    onError={() => setImgError(true)}
                />
            ) : (
                <AvatarIcon index={avatarIndex} />
            )}
          </div>
        </div>
      </div>
  );
}
